import { Card, Col, notification, Row, Space, Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import {
    BasicProfileDto,
    ShiftDetailsDto,
    ShiftListItemDto,
    ShiftProposalDto,
    ShiftProposalStatus,
    ShiftRequestAbbrDto,
    ShiftRequestStatus,
} from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';
import { getShiftProposalInfoAsShiftData, toDollarAmount } from '@clh/util';

import { getDateStrWithTimezone } from '../../helpers';
import { useSelection } from '../../hooks/use-selection';

const ProposalStatusTag = ({ status }: { status: ShiftProposalStatus }) => {
    const getStatusConfig = (status: ShiftProposalStatus) => {
        switch (status) {
            case ShiftProposalStatus.Accepted:
                return { color: 'success', text: 'Approved' };
            case ShiftProposalStatus.Rejected:
                return { color: 'error', text: 'Denied' };
            case ShiftProposalStatus.Pending:
                return { color: 'processing', text: 'Submitted' };
            case ShiftProposalStatus.Expired:
                return { color: 'default', text: 'Expired' };
            case ShiftProposalStatus.Canceled:
                return { color: 'default', text: 'Canceled' };
            default:
                return { color: 'default', text: status };
        }
    };

    const config = getStatusConfig(status);
    return <Tag color={config.color}>{config.text}</Tag>;
};

const RequestStatusTag = ({ status }: { status: ShiftRequestStatus }) => {
    const getStatusConfig = (status: ShiftRequestStatus) => {
        switch (status) {
            case ShiftRequestStatus.Accepted:
                return { color: 'success', text: 'Accepted' };
            case ShiftRequestStatus.Declined:
                return { color: 'error', text: 'Declined' };
            case ShiftRequestStatus.Requested:
                return { color: 'processing', text: 'Requested' };
            case ShiftRequestStatus.Canceled:
                return { color: 'default', text: 'Canceled' };
            case ShiftRequestStatus.Withdrawn:
                return { color: 'default', text: 'Withdrawn' };
            default:
                return { color: 'default', text: status };
        }
    };

    const config = getStatusConfig(status);
    return <Tag color={config.color}>{config.text}</Tag>;
};

const RequestCard = ({
    request,
    proposal,
    nurseProfile,
    originalShift,
    facility,
}: {
    request: ShiftRequestAbbrDto;
    proposal?: ShiftProposalDto;
    nurseProfile: BasicProfileDto | null;
    originalShift: ShiftListItemDto;
    facility: any;
}) => {
    const proposedData = proposal
        ? getShiftProposalInfoAsShiftData({
              shift: {
                  startTime: originalShift.startTime,
                  endTime: originalShift.endTime,
                  platformFeePct: originalShift.platformFeePct,
                  employerFeePct: originalShift.employerFeePct,
                  payRate: originalShift.payRate,
                  totalRate: originalShift.totalRate,
              },
              proposal: {
                  proposedDate: proposal.proposedDate,
                  proposedStartTime: proposal.proposedStartTime,
                  proposedEndTime: proposal.proposedEndTime,
                  proposedPayRate: proposal.proposedPayRate,
              },
          })
        : null;

    const { timezone } = facility.address;

    return (
        <Card
            size="small"
            title={
                <Space>
                    {nurseProfile
                        ? `${nurseProfile.firstName} ${nurseProfile.lastName}`
                        : 'Loading...'}
                    <Typography.Text type="secondary">
                        Request Status:{' '}
                        <RequestStatusTag status={request.status} />
                    </Typography.Text>
                    {proposal && (
                        <Typography.Text type="secondary">
                            Proposal Status:{' '}
                            <ProposalStatusTag status={proposal.status} />
                        </Typography.Text>
                    )}
                </Space>
            }
            style={{ marginBottom: 16 }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {proposal && (
                    <>
                        <Typography.Title level={5} style={{ marginTop: 0 }}>
                            Proposal Details
                        </Typography.Title>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Typography.Text strong>
                                    Original
                                </Typography.Text>
                            </Col>
                            <Col span={12}>
                                <Typography.Text strong>
                                    Proposed
                                </Typography.Text>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Space direction="vertical" size="small">
                                    <div>
                                        <Typography.Text type="secondary">
                                            Shift Date
                                        </Typography.Text>
                                        <br />
                                        {
                                            getDateStrWithTimezone(
                                                originalShift.startTime,
                                                timezone
                                            ).split(',')[0]
                                        }
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Shift Time
                                        </Typography.Text>
                                        <br />
                                        {DateTime.fromJSDate(
                                            originalShift.startTime
                                        )
                                            .setZone(timezone)
                                            .toFormat('HH:mm')}{' '}
                                        -{' '}
                                        {DateTime.fromJSDate(
                                            originalShift.endTime
                                        )
                                            .setZone(timezone)
                                            .toFormat('HH:mm')}
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Total Hourly Cost to Facility
                                        </Typography.Text>
                                        <br />
                                        {toDollarAmount(
                                            originalShift.totalRate
                                        )}
                                        /hour
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Nurse Hourly Rate
                                        </Typography.Text>
                                        <br />
                                        {toDollarAmount(originalShift.payRate)}
                                        /hour
                                    </div>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" size="small">
                                    <div>
                                        <Typography.Text type="secondary">
                                            Shift Date
                                        </Typography.Text>
                                        <br />
                                        {proposal.proposedDate
                                            ? getDateStrWithTimezone(
                                                  proposedData!.startTime,
                                                  timezone
                                              ).split(',')[0]
                                            : 'N/A'}
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Shift Time
                                        </Typography.Text>
                                        <br />
                                        {proposal.proposedStartTime ||
                                        proposal.proposedEndTime ? (
                                            <>
                                                {DateTime.fromJSDate(
                                                    proposedData!.startTime
                                                )
                                                    .setZone(timezone)
                                                    .toFormat('HH:mm')}{' '}
                                                -{' '}
                                                {DateTime.fromJSDate(
                                                    proposedData!.endTime
                                                )
                                                    .setZone(timezone)
                                                    .toFormat('HH:mm')}
                                            </>
                                        ) : (
                                            'N/A'
                                        )}
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Total Hourly Cost to Facility
                                        </Typography.Text>
                                        <br />
                                        {proposal.proposedTotalRate ? (
                                            <>
                                                {toDollarAmount(
                                                    proposal.proposedTotalRate
                                                )}
                                                /hour
                                            </>
                                        ) : (
                                            'N/A'
                                        )}
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary">
                                            Nurse Hourly Rate
                                        </Typography.Text>
                                        <br />
                                        {proposal.proposedPayRate ? (
                                            <>
                                                {toDollarAmount(
                                                    proposedData!.payRate
                                                )}
                                                /hour
                                            </>
                                        ) : (
                                            'N/A'
                                        )}
                                    </div>
                                </Space>
                            </Col>
                        </Row>
                    </>
                )}
                {request.status !== ShiftRequestStatus.Proposed &&
                    request.status !== ShiftRequestStatus.Withdrawn && (
                        <div>
                            <Typography.Text type="secondary">
                                Request first submitted by {request.requestedBy}{' '}
                                {request.createdAt &&
                                    DateTime.fromJSDate(request.createdAt)
                                        .setZone(timezone)
                                        .toFormat('MMM dd, yyyy H:mm a ZZZZ')}
                            </Typography.Text>
                            <br />
                            <Typography.Text type="secondary">
                                Request last updated at{''}
                                {request.updatedAt &&
                                    DateTime.fromJSDate(request.updatedAt)
                                        .setZone(timezone)
                                        .toFormat('MMM dd, yyyy H:mm a ZZZZ')}
                            </Typography.Text>
                        </div>
                    )}
                {proposal?.commentFromFacility && (
                    <Typography.Text type="danger">
                        <b> Facility Comments:</b>
                        <br />
                        {proposal.commentFromFacility}
                    </Typography.Text>
                )}
                {proposal && (
                    <Typography.Text>
                        <b> Nurse Comments:</b>
                        <br />
                        {proposal.commentFromNurse || '(none)'}
                    </Typography.Text>
                )}
            </Space>
        </Card>
    );
};

export default function RequestsTab() {
    const { selection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();
    const [shiftDetails, setShiftDetails] = useState<ShiftDetailsDto | null>(
        null
    );
    const [nurseProfiles, setNurseProfiles] = useState<
        Record<string, BasicProfileDto>
    >({});

    useEffect(() => {
        const fetchShiftDetails = async () => {
            if (!api || !selection?.id) return;

            try {
                const details = await api.shiftControllerViewShift({
                    shiftId: selection.id,
                });
                setShiftDetails(details);

                // Fetch nurse profiles for all requests and proposals
                const nurseProfileIds = new Set([
                    ...details.requests.map((r) => r.nurseProfileId),
                    ...details.proposals.map((p) => p.nurseProfileId),
                ]);

                const profilePromises = Array.from(nurseProfileIds).map(
                    async (profileId) => {
                        try {
                            const profile =
                                await api.profileControllerGetBasicProfile({
                                    profileId,
                                });
                            return { profileId, profile };
                        } catch {
                            return { profileId, profile: null };
                        }
                    }
                );

                const profiles = await Promise.all(profilePromises);
                const profilesMap = profiles.reduce(
                    (acc, { profileId, profile }) => {
                        if (profile) {
                            acc[profileId] = profile;
                        }
                        return acc;
                    },
                    {} as Record<string, BasicProfileDto>
                );

                setNurseProfiles(profilesMap);
            } catch (err) {
                notification.error({ message: unwrapError(err), duration: 3 });
            }
        };

        void fetchShiftDetails();
    }, [api, selection?.id]);

    if (!shiftDetails) {
        return <div>Loading shift requests...</div>;
    }

    if (shiftDetails.requests.length === 0) {
        return (
            <Typography.Text type="secondary">
                No shift requests have been submitted for this shift.
            </Typography.Text>
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Typography.Title level={4}>Shift Requests</Typography.Title>
            <Typography.Text type="secondary">
                {shiftDetails.requests.length} request
                {shiftDetails.requests.length !== 1 ? 's' : ''} submitted
                {shiftDetails.proposals.length > 0 && (
                    <>
                        , {shiftDetails.proposals.length} with proposal
                        {shiftDetails.proposals.length !== 1 ? 's' : ''}
                    </>
                )}
            </Typography.Text>

            {shiftDetails.requests.map((request) => {
                const proposal = shiftDetails.proposals.find(
                    (p) => p.nurseProfileId === request.nurseProfileId
                );

                return (
                    <RequestCard
                        key={`${selection?.id}-${request.nurseProfileId}`}
                        request={request}
                        proposal={proposal}
                        nurseProfile={
                            nurseProfiles[request.nurseProfileId] || null
                        }
                        originalShift={selection}
                        facility={selection?.facility}
                    />
                );
            })}
        </Space>
    );
}
